import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Download, Trash2, Receipt, Calendar, Building2, FileText, Clock, User, DollarSign, Tag, Banknote, Smartphone, CreditCard, Image, Mail, MessageCircle, Loader2 } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import { expensesAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';

const PAYMENT_MODE_CONFIG = {
  CASH: { icon: Banknote, label: 'Cash', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  cash: { icon: Banknote, label: 'Cash', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  UPI: { icon: Smartphone, label: 'UPI', bg: 'bg-teal-100', text: 'text-teal-700' },
  upi: { icon: Smartphone, label: 'UPI', bg: 'bg-teal-100', text: 'text-teal-700' },
  BANK: { icon: Building2, label: 'Bank Transfer', bg: 'bg-blue-100', text: 'text-blue-700' },
  bank_transfer: { icon: Building2, label: 'Bank Transfer', bg: 'bg-blue-100', text: 'text-blue-700' },
  CARD: { icon: CreditCard, label: 'Card', bg: 'bg-amber-100', text: 'text-amber-700' },
  card: { icon: CreditCard, label: 'Card', bg: 'bg-amber-100', text: 'text-amber-700' },
  CHEQUE: { icon: FileText, label: 'Cheque', bg: 'bg-slate-100', text: 'text-slate-700' },
  cheque: { icon: FileText, label: 'Cheque', bg: 'bg-slate-100', text: 'text-slate-700' },
  OTHER: { icon: DollarSign, label: 'Other', bg: 'bg-gray-100', text: 'text-gray-700' },
  other: { icon: DollarSign, label: 'Other', bg: 'bg-gray-100', text: 'text-gray-700' },
};

const CATEGORY_COLORS = {
  'Rent': 'bg-violet-100 text-violet-700',
  'Electricity': 'bg-yellow-100 text-yellow-700',
  'Salaries & Advances': 'bg-blue-100 text-blue-700',
  'Raw Material': 'bg-emerald-100 text-emerald-700',
  'Transportation': 'bg-orange-100 text-orange-700',
  'Marketing': 'bg-pink-100 text-pink-700',
  'Office Supplies': 'bg-cyan-100 text-cyan-700',
  'Bank Fees': 'bg-red-100 text-red-700',
  'Insurance': 'bg-indigo-100 text-indigo-700',
  'Maintenance': 'bg-teal-100 text-teal-700',
  'Professional Fees': 'bg-purple-100 text-purple-700',
  'Fuel': 'bg-amber-100 text-amber-700',
  'Internet/Phone': 'bg-sky-100 text-sky-700',
  'Travel': 'bg-rose-100 text-rose-700',
  'Meals': 'bg-lime-100 text-lime-700',
  'Other': 'bg-slate-100 text-slate-700',
};

const ExpenseDetailPage = () => {
  const { businessId, id: expenseId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const bid = businessId || activeBusiness?.id;

  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => {
    if (bid && expenseId) {
      fetchExpense();
    }
  }, [bid, expenseId]);

  const fetchExpense = async () => {
    try {
      setLoading(true);
      const { data } = await expensesAPI.get(expenseId);
      setExpense(data.expense || data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load expense'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    setConfirmModal({
      title: 'Delete Expense',
      description: `Are you sure you want to delete this ${expense?.category || ''} expense? This action cannot be undone.`,
      confirmLabel: 'Delete',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: Trash2,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await expensesAPI.delete(expenseId);
          toast.success('Expense deleted');
          navigate(`/business/${bid}/expenses`);
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to delete expense'));
        }
      },
    });
  };

  const handleDownloadPdf = async () => {
    try {
      const res = await expensesAPI.pdf(expenseId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expense_${expense?.expense_number || expenseId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to download PDF'));
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      const { data } = await expensesAPI.sendEmail(expenseId);
      toast.success(data?.message || 'Email sent');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send email'));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendWhatsApp = async () => {
    try {
      const { data } = await expensesAPI.sendWhatsApp(expenseId);
      toast.success(data?.message || 'WhatsApp sent');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send WhatsApp'));
    }
  };

  const handleBack = () => {
    navigate(`/business/${bid}/expenses`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <div className="w-10 h-10 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[14px] text-slate-500">Loading expense details...</p>
        </div>
      </div>
    );
  }

  if (!expense) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <Receipt className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-[16px] text-slate-600 font-medium">Expense not found</p>
          <button onClick={handleBack} className="mt-4 text-teal-600 hover:underline text-[14px]">
            Back to Expenses
          </button>
        </div>
      </div>
    );
  }

  const modeConfig = PAYMENT_MODE_CONFIG[expense.payment_mode] || PAYMENT_MODE_CONFIG.CASH;
  const ModeIcon = modeConfig.icon;
  const categoryColor = CATEGORY_COLORS[expense.category] || CATEGORY_COLORS['Other'];

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageFromRight">
      {/* PREMIUM HEADER */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="group flex items-center justify-center w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-[20px] font-bold text-slate-800">
                    {expense.expense_number || 'Expense Details'}
                  </h1>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold ${categoryColor}`}>
                    <Tag className="w-3.5 h-3.5" />
                    {expense.category}
                  </span>
                </div>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  Recorded on {formatDate(expense.created_at || expense.date || expense.expense_date)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                title="Send Email"
              >
                {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              </button>
              <button
                onClick={handleSendWhatsApp}
                className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-all duration-200 active:scale-[0.98]"
                title="Send WhatsApp"
              >
                <MessageCircle className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate(`/business/${bid}/expenses/${expenseId}/edit`)}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={handleDelete}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-red-600 bg-white border border-red-300 rounded-xl hover:bg-red-50 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
              <div className="w-px h-8 bg-slate-200 mx-1"></div>
              <button
                onClick={handleDownloadPdf}
                className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 active:scale-[0.98]"
                title="Download PDF"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">

            {/* LEFT COLUMN */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              {/* Vendor Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Vendor</h2>
                </div>
                {expense.vendor_name ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-teal-100 flex items-center justify-center">
                        <span className="text-[16px] font-bold text-teal-700">
                          {expense.vendor_name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-[14px] font-semibold text-slate-800">{expense.vendor_name}</p>
                        <p className="text-[12px] text-slate-500">Vendor</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-[13px] text-slate-500">No vendor specified</p>
                  </div>
                )}
              </div>

              {/* Payment Info Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center shadow-sm">
                    <DollarSign className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Payment Info</h2>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Date</span>
                    <span className="text-[13px] font-medium text-slate-700">{formatDate(expense.date || expense.expense_date)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Payment Mode</span>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${modeConfig.bg} ${modeConfig.text}`}>
                      <ModeIcon className="w-3 h-3" />
                      {modeConfig.label}
                    </span>
                  </div>
                  {(expense.paid_from_account_name || expense.account_id) && (
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] text-slate-500">Paid From</span>
                      <span className="text-[13px] font-medium text-slate-700">{expense.paid_from_account_name || expense.account_id}</span>
                    </div>
                  )}
                  {expense.paid_by_name && (
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] text-slate-500">Paid By</span>
                      <span className="text-[13px] font-medium text-slate-700">{expense.paid_by_name}</span>
                    </div>
                  )}
                  {(expense.reference_no || expense.reference_number) && (
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] text-slate-500">Reference</span>
                      <span className="text-[13px] font-medium text-slate-700">{expense.reference_no || expense.reference_number}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Billable Info */}
              {expense.billable && (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Billable To</h2>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center">
                        <span className="text-[16px] font-bold text-emerald-700">
                          {expense.customer_name?.charAt(0)?.toUpperCase() || 'C'}
                        </span>
                      </div>
                      <div>
                        <p className="text-[14px] font-semibold text-slate-800">{expense.customer_name || 'Customer'}</p>
                        <p className="text-[12px] text-emerald-600 font-medium">Billable Expense</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CENTER COLUMN */}
            <div className="col-span-12 lg:col-span-6">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
                      <Receipt className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-[14px] font-semibold text-slate-800">Expense Details</h2>
                      <p className="text-[12px] text-slate-500">{expense.category}</p>
                    </div>
                  </div>
                </div>
                <div className="p-5 space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <p className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold mb-1">Category</p>
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-semibold ${categoryColor}`}>
                        <Tag className="w-4 h-4" />
                        {expense.category}
                      </span>
                    </div>
                    <div className="p-4 bg-teal-50 rounded-xl">
                      <p className="text-[11px] text-teal-600 uppercase tracking-wide font-semibold mb-1">Amount</p>
                      <p className="text-[22px] font-bold text-teal-700">₹{(expense.amount || 0).toLocaleString('en-IN')}</p>
                    </div>
                  </div>

                  {(expense.notes || expense.description) && (
                    <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                      <p className="text-[11px] text-amber-600 uppercase tracking-wide font-semibold mb-2">Notes</p>
                      <p className="text-[13px] text-amber-800">{expense.notes || expense.description}</p>
                    </div>
                  )}

                  {expense.tags && expense.tags.length > 0 && (
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <p className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold mb-2">Tags</p>
                      <div className="flex flex-wrap gap-2">
                        {expense.tags.map((tag, idx) => (
                          <span key={idx} className="px-2.5 py-1 bg-white border border-slate-200 rounded-full text-[12px] font-medium text-slate-600">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Attachments */}
                {expense.attachments && expense.attachments.length > 0 && (
                  <div className="px-5 py-4 bg-slate-50 border-t border-slate-100">
                    <p className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold mb-3">Attachments</p>
                    <div className="grid grid-cols-3 gap-3">
                      {expense.attachments.map((att, idx) => (
                        <div key={idx} className="p-3 bg-white rounded-xl border border-slate-200 flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                            {(typeof att === 'string' && att.includes('.pdf')) ? (
                              <FileText className="w-4 h-4 text-teal-600" />
                            ) : (
                              <Image className="w-4 h-4 text-teal-600" />
                            )}
                          </div>
                          <span className="text-[12px] font-medium text-slate-700 truncate">{typeof att === 'string' ? att : att.name || 'File'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Activity Timeline */}
                {(expense.activity_log || []).length > 0 && (
                  <div className="px-5 py-4 border-t border-slate-100">
                    <p className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold mb-3">Activity</p>
                    <div className="space-y-3">
                      {(expense.activity_log || []).map((entry, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <div className="w-2 h-2 rounded-full bg-teal-500 mt-1.5 flex-shrink-0"></div>
                          <div>
                            <p className="text-[12px] text-slate-700 font-medium">{entry.action}</p>
                            <p className="text-[11px] text-slate-500">
                              {entry.by} -- {entry.at ? new Date(entry.at).toLocaleString('en-IN') : ''}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              {/* Summary Card */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-5 shadow-sm border border-teal-200/60">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-sm">
                    <DollarSign className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Expense Summary</h2>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Category</span>
                    <span className="text-slate-800 font-medium">{expense.category}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Payment Mode</span>
                    <span className="text-slate-800 font-medium">{modeConfig.label}</span>
                  </div>
                  {expense.billable && (
                    <div className="flex justify-between text-[13px]">
                      <span className="text-slate-600">Billable</span>
                      <span className="text-emerald-600 font-medium">Yes</span>
                    </div>
                  )}
                  {(expense.tax_amount || 0) > 0 && (
                    <>
                      <div className="flex justify-between text-[13px]">
                        <span className="text-slate-600">Subtotal</span>
                        <span className="text-slate-800 font-medium">₹{(expense.amount || 0).toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between text-[13px]">
                        <span className="text-slate-600">Tax</span>
                        <span className="text-slate-800 font-medium">₹{(expense.tax_amount || 0).toLocaleString('en-IN')}</span>
                      </div>
                    </>
                  )}
                  <div className="border-t border-teal-200 pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-700">Total Amount</span>
                      <span className="text-[22px] font-bold text-teal-600">
                        ₹{(expense.total_amount || expense.amount || 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Status</h2>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                    <div>
                      <p className="text-[13px] font-medium text-slate-700">Recorded</p>
                      <p className="text-[11px] text-slate-500">{formatDate(expense.created_at || expense.date || expense.expense_date)}</p>
                    </div>
                  </div>
                  {expense.updated_at && expense.updated_at !== expense.created_at && (
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <div>
                        <p className="text-[13px] font-medium text-slate-700">Last Updated</p>
                        <p className="text-[11px] text-slate-500">{formatDate(expense.updated_at)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <h2 className="text-[14px] font-semibold text-slate-800 mb-4">Quick Actions</h2>
                <div className="space-y-2">
                  <button
                    onClick={() => navigate(`/business/${bid}/expenses/${expenseId}/edit`)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors duration-200"
                  >
                    <Edit className="w-4 h-4 text-slate-600" />
                    <span className="text-[13px] font-medium text-slate-700">Edit Expense</span>
                  </button>
                  <button
                    onClick={() => navigate(`/business/${bid}/expenses/new`)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl bg-teal-50 hover:bg-teal-100 transition-colors duration-200"
                  >
                    <Receipt className="w-4 h-4 text-teal-600" />
                    <span className="text-[13px] font-medium text-teal-700">Record New Expense</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconBgClass={confirmModal?.iconBgClass}
        iconClass={confirmModal?.iconClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default ExpenseDetailPage;
